/**
 * usePortlessManifest - Portless inbound manifest station hooks
 *
 * Wraps the `customsManifest.portlessStatus` / `customsManifest.sendPortlessNow`
 * tRPC procedures for the packing station's Manifests section. The daily
 * auto-send cron was removed (BLU-600 PR2) — this is now the only send path
 * besides the coordinator settings `portlessExport.runNow`.
 *
 *  - portlessStatus: which client orgs can be sent from this location, each with
 *                     its own last-sent time + pending counts. skipToken when
 *                     no location.
 *  - sendPortlessNow: send ONE client's manifest immediately (bypasses the
 *                      per-day dedupe gate); invalidates portlessStatus so every
 *                      client's last-sent/pending refresh.
 *
 * CROSS-ORG: at a shared 3PL location the caller may be able to send for several
 * client orgs (Thread Lab operating Threadheads + Mini Zali parcels). Each org
 * has its OWN Portless recipient list, so a send is always exactly one client's
 * parcels; `sendPortlessNow` takes the client org id explicitly and the backend
 * re-authorises it against the same `resolveCustomsClients` list that produced
 * `portlessStatus.clients`.
 *
 * FOLLOWS: useCustomsManifest.ts (skipToken query + useUtils invalidation).
 */
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { skipToken } from '@tanstack/react-query';

/** Full Portless status payload for a location (per-client breakdown). */
export type PortlessManifestStatus = RouterOutputs['customsManifest']['portlessStatus'];
/** One client org's slice: org identity, last-sent time and its pending counts. */
export type PortlessManifestClient = Extract<
  PortlessManifestStatus,
  { applicable: true }
>['clients'][number];

/**
 * Query for the Portless manifest status at a location.
 *
 * `applicable` is false for any location that sends Portless manifests for no
 * org the caller may act for — the consuming card renders nothing in that case.
 *
 * @param locationId - The location ID to check, or null (query skipped)
 */
export function usePortlessStatus(locationId: string | null) {
  return trpc.customsManifest.portlessStatus.useQuery(
    locationId ? { locationId } : skipToken,
    {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }
  );
}

/**
 * Mutation to send ONE client org's Portless manifest now
 * (`{ locationId, clientOrganizationId }`). Invalidates portlessStatus so every
 * client's last-sent time and pending counts refresh.
 */
export function useSendPortlessNow() {
  const utils = trpc.useUtils();

  return trpc.customsManifest.sendPortlessNow.useMutation({
    onSuccess: () => {
      utils.customsManifest.portlessStatus.invalidate();
    },
  });
}
